
import React, { useState, useRef, useEffect } from 'react';
import {
  Plus, Search, Save, ArrowLeft,
  Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight, Strikethrough,
  Loader2, CheckCircle2, FileEdit, User as UserIcon, FileText,
  Image as ImageIcon, Link as LinkIcon, Table as TableIcon,
  Minus, RotateCcw, RotateCw, Printer, Trash2, LayoutTemplate,
  Settings, ChevronDown, Upload, Copy, Grid, Sparkles, Briefcase, ChevronRight, Check, LineChart, Code, X, Share2, Wand2, Folder
} from 'lucide-react';
import { User, Vacancy } from '../types';
import { supabase_get_documents, supabase_get_document_files, supabase_save_document, supabase_delete_document, supabase_upload_image, supabase, supabase_get_vacancies, openStorageDocument } from '../services/supabaseService';
import { generateTailoredCV, refineWebsiteText } from '../services/geminiService';
import { generateWordDocument } from '../services/wordTemplateService';
import { uploadToOneDrive } from '../services/microsoftGraphService';
import ShareModal from './ShareModal';

interface Doc {
  id: string;
  client_id?: string;
  title: string;
  author_name: string;
  content: string;
  updated_at: string;
  status: 'Draft' | 'Final';
  is_template?: boolean;
  candidate_id?: string;
  is_file?: boolean; // Discriminator
  file_type?: string;
  storage_path?: string;
  version?: number;
  user_id?: string;
}

interface FileDoc extends Doc {
  is_file: true;
  storage_path: string;
  version: number;
  file_type: string;
}

interface DocsManagerProps {
  user: User | null;
  initialData?: { title: string, content: string, candidateId?: string } | null;
  initialFileId?: string;
  onClearData?: () => void;
  onBack?: () => void;
  candidateId?: string | null;
  onOpenDocumentBuilder?: (docContent: any) => void;
}

const CV_SECTIONS = [
  {
    label: 'Header (Modern)',
    html: '<div style="margin-bottom: 24px; border-bottom: 2px solid #000; padding-bottom: 24px;"> <h1 style="margin: 0 0 8px 0; font-size: 2.5em; line-height: 1;">Voornaam Achternaam</h1> <p style="margin: 0 0 16px 0; font-size: 1.2em; color: #64748b;">Gewenste Functie / Expertise</p> <div style="font-size: 0.9em; display: flex; gap: 16px; color: #475569;"> <span>📍 Stad, Land</span> <span>📧 email@example.com</span> <span>📱 06-12345678</span> </div> </div>'
  },
  {
    label: 'Profielschets',
    html: '<div style="margin-bottom: 24px;"> <table style="width: 100%; border-collapse: collapse;"> <tr> <td style="width: 30%; vertical-align: top; background-color: #64748b; color: white; padding: 12px; font-weight: bold; text-transform: uppercase;">Profiel</td> <td style="width: 70%; vertical-align: top; padding: 12px; background-color: #f8fafc; color: #334155; line-height: 1.6;"> Gedreven professional met meer dan [X] jaar ervaring in [Vakgebied]. Bewezen track record in het realiseren van [Resultaat]. Sterk in [Skill 1] en [Skill 2]. Zoekt naar een uitdaging waarin [Doel] centraal staat. </td> </tr> </table> </div>'
  },
  {
    label: 'Werkervaring (Tijdlijn)',
    html: '<div style="margin-bottom: 24px;"> <table style="width: 100%; border-collapse: collapse;"> <tr> <td style="width: 30%; vertical-align: top; background-color: #64748b; color: white; padding: 12px; font-weight: bold; text-transform: uppercase;">Werkervaring</td> <td style="width: 70%; vertical-align: top; padding: 12px; background-color: #f8fafc; color: #334155;"> <div style="margin-bottom: 16px;"> <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px;"> <strong style="font-size: 1.1em;">Functietitel</strong> <span style="font-size: 0.85em; color: #64748b;">Jan 2022 - Heden</span> </div> <div style="font-style: italic; margin-bottom: 8px; color: #475569;">Bedrijfsnaam, Stad</div> <ul style="margin: 0; padding-left: 20px; line-height: 1.6;"> <li>Verantwoordelijk voor [Taak A] wat leidde tot [Resultaat].</li> <li>Leiding gegeven aan een team van [X] personen.</li> <li>Succesvolle implementatie van [Project X].</li> </ul> </div> </td> </tr> </table> </div>'
  },
  {
    label: 'Opleiding',
    html: '<div style="margin-bottom: 24px;"> <table style="width: 100%; border-collapse: collapse;"> <tr> <td style="width: 30%; vertical-align: top; background-color: #64748b; color: white; padding: 12px; font-weight: bold; text-transform: uppercase;">Opleiding</td> <td style="width: 70%; vertical-align: top; padding: 12px; background-color: #f8fafc; color: #334155;"> <div style="margin-bottom: 12px;"> <div style="display: flex; justify-content: space-between; align-items: baseline;"> <strong>Naam Opleiding / Master</strong> <span style="font-size: 0.85em; color: #64748b;">2018 - 2022</span> </div> <div><em>Onderwijsinstelling</em></div> <div style="font-size: 0.9em; color: #64748b; margin-top: 4px;">Specialisatie: [Specialisatie]</div> </div> </td> </tr> </table> </div>'
  },
  {
    label: 'Skills (Tags)',
    html: '<div style="margin-bottom: 24px;"> <table style="width: 100%; border-collapse: collapse;"> <tr> <td style="width: 30%; vertical-align: top; background-color: #64748b; color: white; padding: 12px; font-weight: bold; text-transform: uppercase;">Vaardigheden</td> <td style="width: 70%; vertical-align: top; padding: 12px; background-color: #f8fafc; color: #334155;"> <div style="display: flex; flex-wrap: wrap; gap: 8px;"> <span style="background: #e2e8f0; color: #334155; padding: 4px 10px; border-radius: 6px; font-size: 0.9em; font-weight: 500;">Project Management</span> <span style="background: #e2e8f0; color: #334155; padding: 4px 10px; border-radius: 6px; font-size: 0.9em; font-weight: 500;">Leiderschap</span> <span style="background: #e2e8f0; color: #334155; padding: 4px 10px; border-radius: 6px; font-size: 0.9em; font-weight: 500;">Strategisch Advies</span> <span style="background: #e2e8f0; color: #334155; padding: 4px 10px; border-radius: 6px; font-size: 0.9em; font-weight: 500;">React & TypeScript</span> </div> </td> </tr> </table> </div>'
  },
  {
    label: '2 Kolommen Layout',
    html: '<div style="display: flex; gap: 32px; margin-bottom: 24px;"> <div style="flex: 1;"> <h3>Linker Kolom</h3> <p>Plaats hier content voor de linkerzijde.</p> </div> <div style="flex: 1;"> <h3>Rechter Kolom</h3> <p>Plaats hier content voor de rechterzijde.</p> </div> </div>'
  },
  {
    label: 'Tabel met Styling',
    html: '<div style="margin-bottom: 24px; overflow: hidden; border: 1px solid #e2e8f0; border-radius: 8px;"> <table style="width: 100%; border-collapse: collapse; font-size: 0.9em; text-align: left;"> <thead> <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0;"> <th style="padding: 12px 16px; font-weight: 600; color: #334155;">Header 1</th> <th style="padding: 12px 16px; font-weight: 600; color: #334155;">Header 2</th> <th style="padding: 12px 16px; font-weight: 600; color: #334155;">Header 3</th> </tr> </thead> <tbody> <tr style="border-bottom: 1px solid #e2e8f0;"> <td style="padding: 12px 16px; color: #64748b;">Rij 1, Cel 1</td> <td style="padding: 12px 16px; color: #64748b;">Rij 1, Cel 2</td> <td style="padding: 12px 16px; color: #64748b;">Rij 1, Cel 3</td> </tr> <tr style="border-bottom: 1px solid #e2e8f0; background-color: #f8fafc;"> <td style="padding: 12px 16px; color: #64748b;">Rij 2, Cel 1 (Zebra)</td> <td style="padding: 12px 16px; color: #64748b;">Rij 2, Cel 2</td> <td style="padding: 12px 16px; color: #64748b;">Rij 2, Cel 3</td> </tr> <tr> <td style="padding: 12px 16px; color: #64748b;">Rij 3, Cel 1</td> <td style="padding: 12px 16px; color: #64748b;">Rij 3, Cel 2</td> <td style="padding: 12px 16px; color: #64748b;">Rij 3, Cel 3</td> </tr> </tbody> </table> </div>'
  },
  {
    label: 'Werkervaring Tabel (Loop)',
    html: '<div style="margin-bottom: 24px;"> <table style="width: 100%; border-collapse: collapse;"> <tr> <td style="width: 30%; vertical-align: top; background-color: #64748b; color: white; padding: 12px; font-weight: bold; text-transform: uppercase;"> (Relevante)<br/>Werkervaring </td> <td style="width: 70%; vertical-align: top; padding: 12px; background-color: #f8fafc; color: #334155;"> {#experience}<br/> <p style="margin: 0 0 4px 0;"><strong>{role}</strong> at <strong>{company}</strong> ({period})</p> <p style="margin: 0 0 12px 0; color: #475569;">{description}</p> {/experience} </td> </tr> </table> </div>'
  }
];

const DocsManager: React.FC<DocsManagerProps> = ({ user, initialData, initialFileId, onClearData, onBack, candidateId, onOpenDocumentBuilder }) => {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [fileDocs, setFileDocs] = useState<Doc[]>([]); // OnlyOffice files

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'documents' | 'templates'>('documents');

  // Editor State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState<Doc | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageMargin, setPageMargin] = useState(96); // Default margin (px)
  const [showTemplates, setShowTemplates] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [savedRange, setSavedRange] = useState<Range | null>(null);

  // Shortcode Tag Helper
  const [showShortcodes, setShowShortcodes] = useState(false);

  // AI Matching State
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [selectedVacancyId, setSelectedVacancyId] = useState<string>('');
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [tailorResult, setTailorResult] = useState<any>(null);
  const [candidateProfile, setCandidateProfile] = useState<any>(null);

  // OnlyOffice State removed

  // Share Modal State
  const [shareConfig, setShareConfig] = useState<{
    isOpen: boolean;
    docId: string;
    is_file: boolean;
    visibility: 'private' | 'company' | 'shared' | 'reviewers_only' | 'subcontractors';
    sharedUsers: string[];
    sharedClients?: string[];
  }>({
    isOpen: false,
    docId: '',
    is_file: false,
    visibility: 'private',
    sharedUsers: [],
    sharedClients: []
  });

  // --- AI Wand (Froukje) Menu State ---
  const [selectionRange, setSelectionRange] = useState<Range | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number, left: number } | null>(null);
  const [showToneMenu, setShowToneMenu] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);

  useEffect(() => {
    const handleSelectionChange = () => {
      if (!isEditorOpen || !editorRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        setMenuPosition(null);
        setSelectionRange(null);
        setShowToneMenu(false);
        return;
      }

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      if (editorRef.current.contains(range.commonAncestorContainer)) {
        setSelectionRange(range);
        setMenuPosition({
          top: rect.top - 50 + window.scrollY,
          left: rect.left + (rect.width / 2)
        });
      } else {
        setMenuPosition(null);
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [isEditorOpen]);

  const handleAiAction = async (action: string, param?: string) => {
    if (!selectionRange) return;

    const selectedText = selectionRange.toString();
    if (!selectedText.trim()) return;

    setIsAiLoading(true);
    try {
      let instruction = "";
      switch (action) {
        case 'edit': instruction = "Corrigeer de spelling, grammatica en zinsbouw van deze tekst. Behoud de inhoud."; break;
        case 'rewrite': instruction = "Herschrijf deze tekst om het vloeiender en wervender te maken."; break;
        case 'shorter': instruction = "Maak deze tekst korter en krachtiger zonder essentiële informatie te verliezen."; break;
        case 'longer': instruction = "Breid deze tekst uit met relevante details en heldere uitleg."; break;
        case 'tone': instruction = `Herschrijf deze tekst in een ${param} toon.`; break;
        case 'custom': instruction = param || "Verbeter deze tekst op natuurlijke wijze."; break;
      }

      const refined = await refineWebsiteText(selectedText, instruction);

      if (refined) {
        selectionRange.deleteContents();
        selectionRange.insertNode(document.createTextNode(refined));
        updateContentState();
      }
    } catch (e) {
      console.error("AI Error:", e);
    } finally {
      setIsAiLoading(false);
      setMenuPosition(null);
    }
  };

  const handleCustomAiRewrite = () => {
    const prompt = window.prompt("Waar wil je dat Froukje AI op let of verandert in de geselecteerde tekst?");
    if (prompt) {
      handleAiAction('custom', prompt);
    }
  };

  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadDocs();
  }, [user, viewMode]);

  // Handle Initial File ID (Open OnlyOffice)
  useEffect(() => {
    let isMounted = true;
    const handleInitialFile = async () => {
      if (!initialFileId) return;

      let found = fileDocs.find(f => f.id === initialFileId);

      if (!found && !loading) {
        try {
          // Fetch directly from database, in case it's a CV Template not in the standard list
          const { data } = await supabase.from('document_files').select('*').eq('id', initialFileId).single();
          if (data) {
            found = {
              id: data.id,
              title: data.title,
              author_name: user?.name || 'Onbekend',
              content: '',
              updated_at: data.updated_at,
              status: 'Draft',
              is_template: data.is_template,
              is_file: true,
              storage_path: data.storage_path,
              version: data.version,
              file_type: data.file_type
            } as any;
          }
        } catch (e) {
          console.error("Failed to load specific document:", e);
        }
      }

      if (!found && loading) return;

      // If found and we are clearing data
      if (found && isMounted && onClearData) {
        onClearData();
      }
    };

    handleInitialFile();
    return () => { isMounted = false; };
  }, [initialFileId, fileDocs, loading]);

  useEffect(() => {
    if (initialData && user && !initialFileId) { // Only if NOT opening a file
      const newDoc: Doc = {
        id: crypto.randomUUID(),
        client_id: user.clientId,
        title: initialData.title,
        author_name: user.name || 'Onbekend',
        content: initialData.content,
        updated_at: new Date().toISOString(),
        status: 'Draft',
        is_template: false, // Ensure it's treated as a new doc instance
        candidate_id: initialData.candidateId // Preserve candidate link
      };
      // Reset editor state first to force re-render/cleanup
      setActiveDoc(null);
      setIsEditorOpen(false);

      // Then set new doc
      setTimeout(() => {
        setActiveDoc(newDoc);
        setDocTitle(newDoc.title);
        setIsEditorOpen(true);
        if (onClearData) onClearData();
      }, 50);
    }
  }, [initialData, user]);

  const loadDocs = async () => {
    setLoading(true);
    if (user?.clientId) {
      try {
        const isTemplate = viewMode === 'templates';

        // 1. Fetch HTML Docs
        const data = await supabase_get_documents(user.clientId, isTemplate);
        if (data !== null) {
          setDocs(data);
        }

        // 2. Fetch OnlyOffice Files (if not in template mode for now)
        if (!isTemplate && user.id) {
          const files = await supabase_get_document_files(user.id);

          if (files) {
            // Add is_file flag and map to Doc type structure roughly
            const mappedFiles: Doc[] = files.map((f: any) => ({
              id: f.id,
              title: f.title,
              author_name: user.name,
              content: '', // No content for file docs
              updated_at: f.updated_at,
              status: 'Draft',
              is_template: false,
              is_file: true,
              storage_path: f.storage_path,
              version: f.version,
              file_type: f.file_type
            }));
            setFileDocs(mappedFiles);
          }
        }

        // 3. Fetch Vacancies for AI Tailoring
        const fetchedVacancies = await supabase_get_vacancies(user.clientId);
        if (fetchedVacancies) {
          setVacancies(fetchedVacancies);
        }

      } catch (e) {
        console.error("Docs load error", e);
      }
    }
    setLoading(false);
  };

  // Fetch candidate profile when a doc is opened that belongs to a candidate
  useEffect(() => {
    const fetchCandidateProfile = async () => {
      const cid = activeDoc?.candidate_id || candidateId;
      if (cid) {
        try {
          const { data } = await supabase.from('candidates').select('*').eq('id', cid).single();
          if (data) {
            // Build a simplified profile from parsed data
            setCandidateProfile({
              name: data.name,
              role: data.parsed_cv?.role || data.ai_parsed_data?.role || '',
              summary: data.parsed_cv?.summary || data.ai_parsed_data?.summary || '',
              skills: data.parsed_cv?.tags || data.ai_parsed_data?.tags || [],
              timeline: data.parsed_cv?.timeline || data.ai_parsed_data?.timeline || [] // Array of {role, company, period, description}
            });
          }
        } catch (e) {
          console.error("Failed to load candidate profile for AI", e);
        }
      } else {
        setCandidateProfile(null);
      }
    };

    fetchCandidateProfile();
  }, [activeDoc?.candidate_id, candidateId]);

  // Selection Helpers
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      if (editorRef.current && editorRef.current.contains(sel.anchorNode)) {
        setSavedRange(sel.getRangeAt(0));
      }
    }
  };

  const ensureEditorFocus = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      // If no saved range, or saved range is invalid, move caret to end
      if (!savedRange) {
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false); // false = to end
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
          setSavedRange(range);
        }
      } else {
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(savedRange);
        }
      }
    }
  };

  // CRITICAL: Sync DOM to State immediately
  const updateContentState = () => {
    if (editorRef.current && activeDoc) {
      const newContent = editorRef.current.innerHTML;
      setActiveDoc({ ...activeDoc, content: newContent });
    }
  };
  // AI Match logic
  const aiName = user?.branding?.aiRecruiter?.name || 'Froukje';
  const aiAvatar = user?.branding?.aiRecruiter?.avatarUrl;

  const handleGenerateMatch = async () => {
    if (!selectedVacancyId || !candidateProfile) return;
    setIsAiProcessing(true);
    try {
      const selectedVacancy = vacancies.find(v => v.id === selectedVacancyId);
      const res = await generateTailoredCV(candidateProfile, selectedVacancy, aiName);
      setTailorResult(res);
    } catch (error) {
      console.error("Tailoring failed", error);
      alert("Kon CV niet optimaliseren.");
    } finally {
      setIsAiProcessing(false);
    }
  };

  const handleCreateDoc = () => {
    const newDoc: Doc = {
      id: crypto.randomUUID(), // Proper UUID
      client_id: user?.clientId,
      title: 'Naamloos document',
      author_name: user?.name || 'Onbekend',
      content: '<p>Start hier met typen...</p>',
      updated_at: new Date().toISOString(),
      status: 'Draft',
      is_template: viewMode === 'templates',
      candidate_id: candidateId || undefined
    };
    setActiveDoc(newDoc);
    setDocTitle(newDoc.title);
    setPageMargin(96);
    setIsEditorOpen(true);
  };

  const handleCreateOnlyOfficeDoc = async () => {
    // Deprecated
  };

  const handleOpenDoc = (doc: Doc) => {
    try {
      if (doc.content && typeof doc.content === 'string' && doc.content.includes('_isDocumentBuilder')) {
          if (onOpenDocumentBuilder) {
              onOpenDocumentBuilder({
                  title: doc.title,
                  content: doc.content,
                  candidateId: doc.candidate_id || candidateId,
                  fileId: doc.id
              });
          }
          return;
      }
    } catch(e) {}

    setActiveDoc(doc);
    setDocTitle(doc.title);
    setPageMargin(96);
    setIsEditorOpen(true);
  };

  const generateWordCVBytes = (buffer: ArrayBuffer, data: any) => {
    return generateWordDocument(buffer, data);
  }

  const handleOpenFileDoc = async (doc: Doc) => {
    if (!doc.storage_path) return;
    try {
      if (candidateProfile) {
        // If a candidate is selected, generate a populated Word document!
        setLoading(true);

        // 1. Fetch template buffer
        const { data: fileData, error: fileError } = await supabase.storage.from('documents').download(doc.storage_path);
        if (fileError || !fileData) throw fileError;
        const templateBuffer = await fileData.arrayBuffer();

        // 2. Prepare Data
        const c = candidateProfile?.candidate || candidateProfile?.candidates || {};
        const parsed = candidateProfile?.ai_parsed_data || {};

        const workHistory = (parsed.experience || []).map((exp: any) => ({
          role: exp.role || exp.title || '',
          company: exp.company || '',
          period: exp.period || exp.year || '',
          description: exp.description || ''
        }));

        const education = (parsed.education || []).map((edu: any) => ({
          degree: edu.degree || edu.opleiding || '',
          school: edu.school || edu.instituut || '',
          period: edu.year || edu.periode || ''
        }));

        const templateData = {
          name: c.name || '',
          email: c.email || '',
          phone: c.phone || '',
          role: c.role || parsed.role || '',
          location: parsed.personal_info?.location || '',
          summary: parsed.summary || c.cvSummary || '',
          skills: (parsed.skills || []).map((s: any) => typeof s === 'string' ? s : s.name).join(', '),
          workHistory: workHistory,
          education: education
        };

        // 3. Generate Doc
        const newDocBlob = generateWordCVBytes(templateBuffer, templateData);

        // 4. Upload to OneDrive
        const candidateNameStr = c.name || 'document';
        const fileName = `${candidateNameStr} CV.docx`.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();

        const { webUrl, error: uploadErr } = await uploadToOneDrive(newDocBlob, fileName);

        if (uploadErr) {
          console.error("OneDrive upload error", uploadErr);
          alert("Fout bij opslaan in de OneDrive: Microsoft account is mogelijk niet gekoppeld.");
        } else if (webUrl) {
          // Success! Open it
          window.open(webUrl, '_blank');
        }

      } else {
        // No candidate selected: open raw file with retry logic
        await openStorageDocument(doc.storage_path);
      }
    } catch (e) {
      console.error(e);
      alert('Bestand kon niet worden geopend.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (asTemplate = false) => {
    if (!activeDoc || !editorRef.current) return;
    setIsSaving(true);

    try {
      const content = editorRef.current.innerHTML;
      const payload = {
        ...activeDoc,
        title: docTitle,
        content,
        updated_at: new Date().toISOString(),
        is_template: asTemplate ? true : activeDoc.is_template,
        candidate_id: activeDoc.candidate_id || candidateId || undefined
      };
      console.log("DocsManager: Initiating save...", payload);
      const saved = await supabase_save_document(payload);
      console.log("DocsManager: Save complete", saved);

      if (asTemplate && !activeDoc.is_template) {
        // If saving as template from a normal doc, we switch context
        setSuccessMessage("Opgeslagen als template!");
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
          handleCloseEditor();
          setViewMode('templates');
        }, 1000);
      } else {
        // Regular save
        setDocs(prev => {
          const exists = prev.find(d => d.id === saved.id || (activeDoc.id.length < 10 && d.id === activeDoc.id));
          if (exists) {
            return prev.map(d => d.id === exists.id ? saved : d);
          }
          return [saved, ...prev];
        });
        setActiveDoc(saved);

        // If it was linked to a candidate, show a clear success message
        setSuccessMessage(activeDoc.candidate_id ? "Document succesvol opgeslagen!" : "Wijzigingen opgeslagen.");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 2000);
      }
    } catch (e) {
      alert("Opslaan mislukt");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm("Document verwijderen?")) {
      await supabase_delete_document(id);
      setDocs(prev => prev.filter(d => d.id !== id));
    }
  };

  const handleCloseEditor = () => {
    if (initialData && onBack) {
      onBack();
    } else {
      setIsEditorOpen(false);
      setActiveDoc(null);
      loadDocs();
    }
  };

  // --- EDITOR COMMANDS ---
  const execCmd = (command: string, value: string | undefined = undefined) => {
    ensureEditorFocus();
    document.execCommand(command, false, value);
    updateContentState(); // Sync state immediately
  };

  const triggerImageUpload = () => {
    saveSelection();
    fileInputRef.current?.click();
  };

  const insertImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setIsUploading(true);
      try {
        const url = await supabase_upload_image(e.target.files[0]);
        if (url) {
          // Explicitly refocus the editor before inserting
          ensureEditorFocus();

          // Use insertHTML instead of insertImage for better control and reliability
          const imgHtml = `<img src="${url}" style="max-width: 100%; height: auto; margin: 10px 0; border-radius: 4px;" />`;
          document.execCommand('insertHTML', false, imgHtml);
          updateContentState(); // Sync state immediately
        }
      } catch (error) {
        console.error("Image upload failed", error);
        alert("Afbeelding uploaden mislukt.");
      } finally {
        setIsUploading(false);
        e.target.value = ''; // Reset so same file can be selected again
      }
    }
  };

  const insertTable = () => {
    saveSelection();
    const rows = prompt("Aantal rijen:", "3");
    const cols = prompt("Aantal kolommen:", "3");
    ensureEditorFocus();

    if (rows && cols) {
      let html = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #e2e8f0;"><tbody>';
      for (let r = 0; r < parseInt(rows); r++) {
        html += '<tr>';
        for (let c = 0; c < parseInt(cols); c++) {
          html += '<td style="border: 1px solid #e2e8f0; padding: 12px; vertical-align: top;">Cel</td>';
        }
        html += '</tr>';
      }
      html += '</tbody></table><p><br/></p>';
      document.execCommand('insertHTML', false, html);
      updateContentState(); // Sync state
    }
  };

  const insertLink = () => {
    saveSelection();
    const url = prompt("URL invoeren:", "https://");
    ensureEditorFocus();
    if (url) {
      document.execCommand('createLink', false, url);
      updateContentState(); // Sync state
    }
  };

  const insertTemplate = (html: string) => {
    setShowTemplates(false);
    // Use setTimeout to allow dropdown close animation/event loop to finish before focusing
    setTimeout(() => {
      ensureEditorFocus();
      document.execCommand('insertHTML', false, html + '<p><br/></p>');
      updateContentState(); // Sync state immediately after insert
    }, 50);
  };

  const handlePrint = () => {
    const content = editorRef.current?.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    if (printWindow && content) {
      printWindow.document.write('<html><head><title>Print</title>');
      printWindow.document.write('</head><body style="font-family: Arial, sans-serif; padding: 40px; color: #1e293b;">');
      printWindow.document.write(content);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Prevent focus loss on toolbar clicks
  const preventFocusLoss = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Combine docs for display
  const allDocs = [...fileDocs, ...docs].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  const filteredDocs = allDocs.filter(d => d.title.toLowerCase().includes(searchTerm.toLowerCase()));

  // --- RENDER COLLABORA EDITOR (REMOVED) ---

  // --- EDITOR VIEW (HTML) ---
  if (isEditorOpen) {
    return (
      <div className="fixed inset-0 z-[200] bg-[#F1F5F9] flex flex-col animate-in fade-in duration-200">
        {/* Top Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between shadow-sm shrink-0 h-16 z-20 relative">
          <div className="flex items-center gap-4">
            <button onClick={handleCloseEditor} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div className="flex flex-col">
              <input
                type="text"
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                className="font-bold text-lg text-slate-900 outline-none placeholder:text-slate-400 bg-transparent"
                placeholder="Naamloos document"
              />
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1">
                {isSaving ? 'Opslaan...' : activeDoc?.is_template ? 'Template Mode' : 'Document Mode'}
                {activeDoc?.candidate_id && <span className="bg-red- text-red- px-2 py-0.5 rounded ml-2">Kandidaat Doc</span>}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="p-2 text-slate-500 hover:bg-slate-100 rounded-full" title="Print / PDF"><Printer size={18} /></button>

            {!activeDoc?.is_template && (
              <button onClick={() => handleSave(true)} className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
                <Copy size={14} /> Save as Template
              </button>
            )}

            <button onClick={() => handleSave(false)} className="bg-red- text-white px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest shadow-md hover:bg-red- transition-all flex items-center gap-2 active:scale-95 ml-2">
              {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-slate-50 border-b border-gray-200 p-2 flex flex-wrap items-center justify-center gap-1 shadow-inner shrink-0 relative z-10" onMouseDown={preventFocusLoss}>
          {/* History */}
          <div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2">
            <button onClick={() => execCmd('undo')} className="p-1.5 rounded hover:bg-white text-slate-600" title="Undo"><RotateCcw size={16} /></button>
            <button onClick={() => execCmd('redo')} className="p-1.5 rounded hover:bg-white text-slate-600" title="Redo"><RotateCw size={16} /></button>
          </div>

          {/* Typography */}
          <div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2">
            <select onChange={(e) => execCmd('fontName', e.target.value)} className="h-8 bg-transparent text-xs font-medium border border-slate-200 rounded px-1 outline-none w-24">
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times</option>
              <option value="Courier New">Courier</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
            </select>
            <select onChange={(e) => execCmd('formatBlock', e.target.value)} className="h-8 bg-transparent text-xs font-medium border border-slate-200 rounded px-1 outline-none w-24">
              <option value="p">Normal</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
              <option value="blockquote">Quote</option>
            </select>
          </div>

          {/* Formatting */}
          <div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2">
            <button onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-white text-slate-600 font-bold"><Bold size={16} /></button>
            <button onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-white text-slate-600 italic"><Italic size={16} /></button>
            <button onClick={() => execCmd('underline')} className="p-1.5 rounded hover:bg-white text-slate-600 underline"><Underline size={16} /></button>
            <button onClick={() => execCmd('strikeThrough')} className="p-1.5 rounded hover:bg-white text-slate-600 line-through"><Strikethrough size={16} /></button>
            <input type="color" onChange={(e) => execCmd('foreColor', e.target.value)} className="w-8 h-8 p-1 bg-transparent cursor-pointer border-0" title="Text Color" />
            <input type="color" onChange={(e) => execCmd('hiliteColor', e.target.value)} className="w-8 h-8 p-1 bg-transparent cursor-pointer border-0" title="Highlight Color" defaultValue="#ffff00" />
          </div>

          {/* Alignment */}
          <div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2">
            <button onClick={() => execCmd('justifyLeft')} className="p-1.5 rounded hover:bg-white text-slate-600"><AlignLeft size={16} /></button>
            <button onClick={() => execCmd('justifyCenter')} className="p-1.5 rounded hover:bg-white text-slate-600"><AlignCenter size={16} /></button>
            <button onClick={() => execCmd('justifyRight')} className="p-1.5 rounded hover:bg-white text-slate-600"><AlignRight size={16} /></button>
          </div>

          {/* Inserts */}
          <div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2">
            <button onClick={triggerImageUpload} className="p-1.5 rounded hover:bg-white text-slate-600 flex items-center gap-1" title="Insert Image">
              {isUploading ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
            </button>
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={insertImage} />

            <button onClick={insertTable} className="p-1.5 rounded hover:bg-white text-slate-600" title="Insert Table"><TableIcon size={16} /></button>
            <button onClick={insertLink} className="p-1.5 rounded hover:bg-white text-slate-600" title="Insert Link"><LinkIcon size={16} /></button>
            <button onClick={() => execCmd('insertHorizontalRule')} className="p-1.5 rounded hover:bg-white text-slate-600" title="Horizontal Line"><Minus size={16} /></button>
          </div>

          {/* Templates Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-1 bg-white border border-slate-200 px-3 py-1.5 rounded text-xs font-bold text-slate-700 hover:bg-slate-50"
            >
              <LayoutTemplate size={14} /> Templates <ChevronDown size={12} />
            </button>
            {showTemplates && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                <div className="p-2 border-b border-slate-50 bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-widest">CV Secties</div>
                {CV_SECTIONS.map((section, idx) => (
                  <button
                    key={idx}
                    onClick={() => insertTemplate(section.html)}
                    className="w-full text-left px-4 py-2.5 text-xs font-medium hover:bg-slate-50 text-slate-700 border-b border-slate-50 last:border-0 flex items-center justify-between group"
                  >
                    {section.label}
                    <Plus size={12} className="opacity-0 group-hover:opacity-100 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Editor Area (A4 Page Simulation) */}
        <div className="flex-1 overflow-auto bg-slate-200 p-8 flex justify-center relative" onClick={ensureEditorFocus}>
          <div
            className="bg-white shadow-2xl min-h-[1123px] w-[794px] mx-auto transition-all"
            style={{
              padding: `${pageMargin}px`, // Dynamic margin
              outline: 'none'
            }}
          >
            <div
              ref={editorRef}
              className="w-full h-full outline-none prose prose-slate max-w-none text-sm"
              contentEditable
              suppressContentEditableWarning
              onInput={updateContentState} // Update state on every input
              dangerouslySetInnerHTML={{ __html: activeDoc?.content || '' }}
              style={{ minHeight: '800px' }}
            />
          </div>
        </div>

        {/* Footer Info */}
        <div className="absolute bottom-4 right-8 bg-white/80 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-200 text-[10px] font-mono text-slate-500 shadow-sm pointer-events-none">
          {editorRef.current?.innerText.length || 0} chars • A4 Format
        </div>

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 text-white px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center gap-3 z-[250] animate-scaleIn">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white mb-2">
              <CheckCircle2 size={32} />
            </div>
            <h3 className="text-lg font-black uppercase tracking-tight">Succes!</h3>
            <p className="text-sm font-medium text-slate-300">{successMessage}</p>
          </div>
        )}

        {/* FLOATING AI MENU */}
        {menuPosition && isEditorOpen && (
          <div
            className="fixed z-[300] flex items-center gap-1 p-1 bg-white rounded-xl shadow-2xl shadow-purple-500/20 border border-purple-100 animate-in fade-in zoom-in-95 duration-200"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translateX(-50%)'
            }}
            onMouseDown={(e) => e.preventDefault()} // Prevent losing focus
          >
            <div className="flex items-center gap-1 pr-2 border-r border-slate-100 mr-1">
              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                {isAiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              </div>
              <span className="text-[10px] font-black uppercase text-purple-900 mr-1 tracking-widest">AI</span>
            </div>

            {!showToneMenu ? (
              <>
                <button onClick={() => handleAiAction('edit')} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                  Check
                </button>
                <button onClick={() => handleAiAction('rewrite')} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                  Herschrijf
                </button>
                <button onClick={handleCustomAiRewrite} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 group" title="Eigen instructie...">
                  <Wand2 size={12} className="text-purple-500 group-hover:rotate-12 transition-transform" /> Aangepast
                </button>
                <button onClick={() => setShowToneMenu(true)} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                  Toon <ChevronRight size={12} />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1">
                <button onClick={() => setShowToneMenu(false)} disabled={isAiLoading} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg disabled:opacity-50"><ArrowLeft size={14} /></button>
                <button onClick={() => handleAiAction('tone', 'Formeel')} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Formeel</button>
                <button onClick={() => handleAiAction('tone', 'Vriendelijk')} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Vriendelijk</button>
                <button onClick={() => handleAiAction('tone', 'Wervend')} disabled={isAiLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Wervend</button>
              </div>
            )}
          </div>
        )}

      </div>
    );
  }

  // --- DASHBOARD VIEW ---
  return (
    <div className="flex flex-col bg-slate-50 min-h-[85vh] w-full relative">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 2xl:px-8 2xl:py-6 flex flex-col md:flex-row md:items-center justify-between shadow-sm z-20">
        <div className="mb-4 md:mb-0">
          <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
            <Folder className="text-[#e51b23] shrink-0" size={24} /> Documenten Beheer
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-1">Beheer aanbiedingen, contracten en notities.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
            <button
              onClick={() => setViewMode('documents')}
              className={`px-2 py-1 2xl:px-4 2xl:py-2 rounded-md text-[10px] 2xl:text-xs font-bold transition-all ${viewMode === 'documents' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Mijn Documenten
            </button>
            <button
              onClick={() => setViewMode('templates')}
              className={`px-2 py-1 2xl:px-4 2xl:py-2 rounded-md text-[10px] 2xl:text-xs font-bold transition-all ${viewMode === 'templates' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Templates
            </button>
          </div>
          <button
            onClick={handleCreateDoc}
            className="flex items-center gap-1.5 2xl:gap-2 bg-[#e51b23] text-white px-3 py-1.5 2xl:px-5 2xl:py-2.5 rounded-lg font-black text-[10px] 2xl:text-xs uppercase tracking-widest shadow-md hover:bg-red-700 transition-all active:scale-95 text-nowrap"
          >
            <Plus size={14} className="2xl:w-4 2xl:h-4" /> Web Doc
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 w-full max-w-[1600px] mx-auto">
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Zoek documenten..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 p-4 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-red-0 transition-all shadow-sm"
            />
          </div>
          <div className="w-full md:w-64">
            <div className="relative h-full flex items-center bg-white border border-slate-200 rounded-xl shadow-sm px-4">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-2">Filter Tag:</span>
              <select className="flex-1 bg-transparent text-sm font-bold text-slate-700 outline-none">
                <option value="all">Alle Tags</option>
                <option value="contract">Contracten</option>
                <option value="vog">VOG's</option>
                <option value="id">ID Bewijzen</option>
                <option value="intake">Intakes</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#e51b23] mb-4" size={32} />
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Documenten worden geladen...</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 text-slate-300 shadow-sm border border-slate-100">
              <FileText size={32} />
            </div>
            <p className="text-sm font-bold text-slate-500">Geen documenten gevonden.</p>
            <div className="flex gap-4 mt-4">
              <button onClick={handleCreateDoc} className="text-[#e51b23] text-xs font-bold uppercase tracking-widest hover:underline">Start een nieuw document</button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden relative">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="p-4 w-12"></th>
                    <th className="p-4">Document Naam</th>
                    <th className="p-4 w-40">Type</th>
                    <th className="p-4 w-40">Laatst Gewijzigd</th>
                    <th className="p-4 w-48">Tags</th>
                    <th className="p-4 text-right w-24">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDocs.map((doc: any) => (
                    <tr
                      key={doc.id}
                      className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                      onClick={() => doc.is_file ? handleOpenFileDoc(doc) : handleOpenDoc(doc)}
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border shadow-sm ${doc.is_file
                          ? 'bg-red- text-red- border-red-'
                          : (doc.is_template ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-slate-50 text-slate-600 border-slate-100')
                          }`}>
                          {doc.is_file ? <FileText size={18} /> : (doc.is_template ? <LayoutTemplate size={18} /> : <FileEdit size={18} />)}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-900 group-hover:text-red- transition-colors">{doc.title}</span>
                          <span className="text-[10px] font-medium text-slate-400 mt-0.5 flex items-center gap-1">
                            {doc.candidate_id ? (
                              <><Briefcase size={10} className="text-amber-500" /> Kandidaat Gekoppeld</>
                            ) : (
                              <><span className="opacity-50">Ongekoppeld</span></>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-2 py-1 bg-slate-100 rounded border border-slate-200">
                          {doc.is_file ? 'Word Doc' : (doc.is_template ? 'Template' : (doc.content?.includes('_isDocumentBuilder') ? 'Builder Doc' : 'Web Doc'))}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-xs font-bold text-slate-600">
                          {new Date(doc.updated_at).toLocaleDateString('nl-NL')}
                        </span>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {/* Visual Tag Mocks */}
                          {doc.is_template ? (
                            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[9px] font-black uppercase tracking-wider rounded border border-purple-200">Standaard</span>
                          ) : doc.is_file ? (
                            <span className="px-2 py-0.5 bg-red- text-red- text-[9px] font-black uppercase tracking-wider rounded border border-red-">Contract</span>
                          ) : doc.content?.includes('_isDocumentBuilder') ? (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-black uppercase tracking-wider rounded border border-blue-200">Profiel</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-black uppercase tracking-wider rounded border border-slate-200">Notitie</span>
                          )}
                          <button className="w-5 h-5 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:text-red- hover:border-red- hover:bg-red- transition-all opacity-0 group-hover:opacity-100 ml-1" title="Tag Toevoegen">
                            <Plus size={10} />
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => {
                            e.stopPropagation();
                            setShareConfig({
                              isOpen: true,
                              docId: doc.id,
                              is_file: !!doc.is_file,
                              visibility: doc.visibility as any || 'private',
                              sharedUsers: doc.shared_with_users || [],
                              sharedClients: []
                            });
                          }} className="text-slate-400 hover:text-red- p-2 rounded-lg hover:bg-red- transition-colors shadow-sm bg-white border border-slate-200/50" title="Delen">
                            <Share2 size={16} />
                          </button>
                          {!doc.is_file && (
                            <button onClick={() => handleOpenDoc(doc)} className="text-slate-400 hover:text-red- p-2 rounded-lg hover:bg-red- transition-colors shadow-sm bg-white border border-slate-200/50" title="Bewerken">
                              <FileEdit size={16} />
                            </button>
                          )}
                          <button onClick={(e) => handleDelete(e, doc.id)} className="text-slate-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors shadow-sm bg-white border border-slate-200/50" title="Verwijderen">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ShareModal
        isOpen={shareConfig.isOpen}
        onClose={() => setShareConfig({ ...shareConfig, isOpen: false })}
        itemId={shareConfig.docId}
        itemType={shareConfig.is_file ? 'document_files' : 'documents'}
        currentVisibility={shareConfig.visibility}
        currentSharedUsers={shareConfig.sharedUsers}
        currentSharedClients={shareConfig.sharedClients}
        clientId={user?.clientId}
        onSave={(visibility, sharedUsers, sharedClients) => {
          // Optimistically update lists
          if (shareConfig.is_file) {
            setFileDocs(prev => prev.map(d => d.id === shareConfig.docId ? { ...d, visibility, shared_with_users: sharedUsers } as any : d));
          } else {
            setDocs(prev => prev.map(d => d.id === shareConfig.docId ? { ...d, visibility, shared_with_users: sharedUsers } as any : d));
          }
          alert("Deel instellingen succesvol opgeslagen.");
        }}
      />
    </div>
  );
};

export default DocsManager;
